import {
  Button,
  ButtonGroup,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { noop } from "lodash";
import { useCallback, useState } from "react";

import {
  useIntercomQuery,
  usePowerCycleIntercomMutation,
  useRestartIntercomMutation,
} from "src/routes/ProductUnits/Intercoms/queries";

type IntercomSupportButtonsProps = {
  intercomId?: string;
};

export const IntercomSupportButtons = ({
  intercomId,
}: IntercomSupportButtonsProps) => {
  if (!intercomId) {
    return null;
  }

  const toast = useToast();
  const [modalName, setModalName] = useState<"restart" | "power-cycle" | null>(
    null
  );
  const {
    isOpen: modalIsOpen,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();

  const onSuccess = useCallback(() => {
    onCloseModal();
    setModalName(null);
    toast({
      description: `Intercom is ${
        modalName === "restart" ? "restarting" : "power cycling"
      }.`,
      status: "success",
      duration: 5000,
    });
  }, [modalName, onCloseModal, toast, setModalName]);

  const intercomQuery = useIntercomQuery(intercomId);
  const restartIntercom = useRestartIntercomMutation(onSuccess);
  const powerCycleIntercom = usePowerCycleIntercomMutation(onSuccess);

  const isMutating = restartIntercom.isLoading || powerCycleIntercom.isLoading;

  const onReset = useCallback(() => {
    onCloseModal();
    setModalName(null);
  }, [onCloseModal, setModalName]);

  const onSubmit = useCallback(() => {
    if (!intercomQuery.data || !intercomQuery.data.intercomActivationId) {
      return;
    }

    const mutation =
      modalName === "restart" ? restartIntercom : powerCycleIntercom;
    return mutation
      .mutateAsync({
        intercomActivationId: intercomQuery.data.intercomActivationId,
      })
      .catch(noop);
  }, [intercomQuery.data, modalName, powerCycleIntercom, restartIntercom]);

  const onButtonClick = useCallback(
    (name: "restart" | "power-cycle") => () => {
      setModalName(name);
      onOpenModal();
    },
    [onOpenModal, setModalName]
  );

  if (
    intercomQuery.isLoading ||
    !intercomQuery.data ||
    !intercomQuery.data.intercomActivationId
  ) {
    return null;
  }

  return (
    <Flex flexDirection="row">
      <Modal isOpen={modalIsOpen} onClose={onReset}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{`${
            modalName === "restart" ? "Restart" : "Power Cycle"
          }`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {modalName === "restart"
              ? "Restarting can take several minutes. Are you sure you want to restart?"
              : "Are you sure you want to power cycle?"}
          </ModalBody>
          <ModalFooter>
            <Button
              color="black"
              colorScheme="brand.lightGray"
              mr={3}
              onClick={onReset}
            >
              Cancel
            </Button>
            <Button
              colorScheme="brand.red"
              onClick={onSubmit}
              isDisabled={isMutating}
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ButtonGroup gap="8px">
        <Button
          colorScheme="brand.blue"
          onClick={onButtonClick("restart")}
          isDisabled={isMutating}
        >
          Software Restart
        </Button>
        <Button
          colorScheme="brand.blueGreen"
          onClick={onButtonClick("power-cycle")}
          isDisabled={isMutating}
        >
          Power Cycle
        </Button>
      </ButtonGroup>
    </Flex>
  );
};
